import { useEffect, useMemo } from 'react'
import { QueryClientProvider } from '@tanstack/react-query'
import { App as CapacitorApp, type URLOpenListenerEvent } from '@capacitor/app'
import { createQueryClient } from '@/api/query-client'
import { AppRouter } from '@/router'
import { Toasts } from '@/components/ui/Toasts'
import { isNative } from '@/lib/platform'
import '@/i18n'
import '@/styles/global.scss'

/**
 * Port of App.vue plus the Quasar boot files.
 *
 * App.vue prefetched the giveaway/prize-type enums and the current user on
 * mount; those are now ordinary queries fetched by the screens that need them,
 * so the app renders without waiting on three requests it may never use.
 */
export function App() {
  const queryClient = useMemo(() => createQueryClient(), [])

  useEffect(() => {
    if (!isNative()) return

    // OAuth on iOS/Android returns through the app's custom scheme
    // (jlm://auth/discord?code=...). Translate that into an in-app route.
    const listener = CapacitorApp.addListener('appUrlOpen', (event: URLOpenListenerEvent) => {
      try {
        const url = new URL(event.url)
        const path = `${url.pathname}${url.search}`
        window.history.pushState({}, '', path.startsWith('/') ? path : `/${path}`)
        window.dispatchEvent(new PopStateEvent('popstate'))
      } catch {
        /* not a URL we recognise */
      }
    })

    return () => {
      void listener.then((handle) => handle.remove())
    }
  }, [])

  return (
    <QueryClientProvider client={queryClient}>
      <AppRouter />
      <Toasts />
    </QueryClientProvider>
  )
}
